import AutoFixHigh from "@mui/icons-material/AutoFixHigh";
import { Button, Tooltip } from "@mui/material";
import { ConfirmDialog } from "components/ConfirmDialog";
import { DynamicFieldFragment } from "generated-graphql/graphql";
import { useState } from "react";
import { FieldValues, useFormContext } from "react-hook-form";
import { isAuthorizationError } from "utils/errorUtils";

import {
  readPrefillSource,
  suggestionForSource,
  usePrefillWasteStateField,
} from "./usePrefillWasteStateField";

export const NO_ACCESS_TOOLTIP = "You don't have access to this facility's waste data.";
export const ERROR_TOOLTIP = "Couldn't load waste data to pre-fill — try again.";
export const NO_DATA_TOOLTIP =
  "No waste data to pre-fill from — add waste lines for this facility first.";

type PrefillWasteFieldsButtonProps = {
  /**
   * The state fields currently rendered in the form, in their react-hook-form
   * array order. Each field's `metadata.prefillSource` (if present) determines
   * whether it is pre-filled and from which waste-data source.
   */
  fields: ReadonlyArray<Pick<DynamicFieldFragment, "metadata">>;
  /** RHF array name the fields live under (e.g. "stateFields"). */
  namePrefix: string;
  facilityId?: string | null;
};

/**
 * A single "Pre-fill from waste data" button that fills every state field
 * tagged with a `prefillSource` (e.g. the AL 8700-12 fields) from one backend
 * query. Renders nothing when there are no prefillable fields or no facility.
 *
 * The suggestions are fetched eagerly when the button mounts so its state can be
 * resolved before the click: it stays disabled (with an explanatory tooltip)
 * while loading, when the user can't read the facility's waste data, on error,
 * or when there is no waste data to pre-fill from.
 *
 * Pre-fill overwrites the target fields, so it asks for confirmation first when
 * any of them already contains text.
 */
export function PrefillWasteFieldsButton({
  fields,
  namePrefix,
  facilityId,
}: PrefillWasteFieldsButtonProps) {
  const { getValues, setValue } = useFormContext<FieldValues>();
  const [showConfirm, setShowConfirm] = useState(false);

  const prefillable = fields.flatMap((field, index) => {
    const source = readPrefillSource(field.metadata);
    return source ? [{ index, source }] : [];
  });

  const { suggestions, loading, error } = usePrefillWasteStateField(
    facilityId,
    prefillable.length === 0
  );

  if (prefillable.length === 0 || !facilityId) {
    return null;
  }

  // Only the prefillable fields that actually have a non-empty suggestion.
  const fillable = suggestions
    ? prefillable.filter(({ source }) => suggestionForSource(suggestions, source) !== "")
    : [];

  let tooltip = "";
  if (isAuthorizationError(error)) {
    tooltip = NO_ACCESS_TOOLTIP;
  } else if (error) {
    tooltip = ERROR_TOOLTIP;
  } else if (!loading && fillable.length === 0) {
    tooltip = NO_DATA_TOOLTIP;
  }

  const disabled = loading || !!error || fillable.length === 0;

  const prefill = () => {
    if (!suggestions) {
      return;
    }
    for (const { index, source } of fillable) {
      setValue(`${namePrefix}.${index}.value`, suggestionForSource(suggestions, source), {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  };

  const onClick = () => {
    // Read current values at click time so freshly-typed text is respected.
    const hasExistingText = prefillable.some(({ index }) => {
      const value = getValues(`${namePrefix}.${index}.value`);
      return typeof value === "string" && value.trim().length > 0;
    });
    if (hasExistingText) {
      setShowConfirm(true);
      return;
    }
    prefill();
  };

  return (
    <>
      <Tooltip title={tooltip} arrow>
        {/* span wrapper so the tooltip still fires while the button is disabled */}
        <span>
          <Button size="small" startIcon={<AutoFixHigh />} onClick={onClick} disabled={disabled}>
            Pre-fill from waste data
          </Button>
        </span>
      </Tooltip>
      <ConfirmDialog
        open={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={() => {
          prefill();
          setShowConfirm(false);
        }}
        header="Replace existing text?"
        info="Pre-filling from waste data will overwrite the text already in these fields. Continue?"
        confirmText="Pre-fill and replace"
        cancelText="Cancel"
      />
    </>
  );
}
